import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { UpdateMentorProfileRequest } from '../../../types';
import ProfileForm from '../../../components/Mentor/ProfileForm';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import { useAuth } from '../../../contexts/AuthContext';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';

const ProfileTab: React.FC = () => {
  const toast = useToast();
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);

  const { data: profileData, isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getMentorProfile();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: categories = [] } = useQuery({
    queryKey: mentorProfileQueryKeys.categories,
    queryFn: async () => {
      const response = await apiService.getCategories();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        websiteUrl: profileData.websiteUrl,
        company: profileData.company,
        city: profileData.city,
        country: profileData.country,
        jobTitle: profileData.jobTitle,
        hourlyRate: profileData.hourlyRate,
        categoryId: profileData.categoryId,
        bio: profileData.bio,
        linkedInProfile: profileData.linkedInProfile,
        githubProfile: profileData.githubProfile,
        twitterProfile: profileData.twitterProfile
      });
    }
  }, [profileData, form]);

  const updateProfileMutation = useMutation({
    mutationFn: async (updateData: UpdateMentorProfileRequest) => {
      const response = await apiService.updateMentorProfile(updateData);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: async (data) => {
      toast.success('Profil başarıyla güncellendi!');
      queryClient.setQueryData(mentorProfileQueryKeys.profile, data);
      await refreshUser();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Profil güncellenirken hata oluştu.');
    }
  });

  const handleSave = (values: Record<string, unknown>) => {
    const updateData: UpdateMentorProfileRequest = {
      firstName: values.firstName as string,
      lastName: values.lastName as string,
      phoneNumber: values.phoneNumber as string,
      websiteUrl: values.websiteUrl as string,
      company: values.company as string,
      city: values.city as string,
      country: values.country as string,
      jobTitle: values.jobTitle as string,
      hourlyRate: values.hourlyRate as number,
      categoryId: values.categoryId as number,
      bio: values.bio as string,
      linkedInProfile: values.linkedInProfile as string,
      githubProfile: values.githubProfile as string,
      twitterProfile: values.twitterProfile as string
    };
    updateProfileMutation.mutate(updateData);
  };

  const imageUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await apiService.uploadMentorProfileImage(file);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: async () => {
      toast.success('Profil fotoğrafı başarıyla güncellendi!');
      await queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.profile });
      await refreshUser();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Fotoğraf yüklenirken hata oluştu.');
    },
    onSettled: () => setUploading(false)
  });

  const handleImageUpload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz.');
      return false;
    }
    setUploading(true);
    imageUploadMutation.mutate(file);
    return false;
  };

  return (
    <PanelTabCard
      title="Profil Bilgileri"
      description="Profil bilgilerinizi güncelleyin ve diğer kullanıcılara nasıl görüneceğinizi belirleyin."
    >
      <ProfileForm
        form={form}
        profileData={profileData ?? null}
        categories={categories}
        loading={loading}
        saving={updateProfileMutation.isPending}
        uploading={uploading}
        onSave={handleSave}
        onImageUpload={handleImageUpload}
      />
    </PanelTabCard>
  );
};

export default ProfileTab;
